/**
 * Pilot navbar — composition on top of the canonical Oatmeal
 * `NavbarWithLogoActionsAndLeftAlignedLinks` section.
 *
 * Phase 1 of the Oatmeal rebuild (`docs/eng/pilot-oatmeal-rebuild.md`)
 * retires the prior 331-line hand-rolled navbar (which existed because
 * `@tailwindplus/elements/react` crashed under CRA's webpack — Phase 0d
 * fixed that root cause). This file is now the thin composition
 * documented by the rebuild's architectural invariant: vendor section
 * stays pristine; pilot-specific behavior (view-mode switching,
 * audience gating, the locked-Sandbox affordance) is wired in via
 * `usePilot()` and rendered into the `logo` / `links` / `actions` slots.
 *
 * What lives here vs the vendor section:
 *
 *   - **Vendor (`apps/web/src/oatmeal/sections/`)** — chrome, sticky
 *     header, responsive breakpoints, mobile `<el-dialog>` menu wiring.
 *   - **Here** — audience-gated nav-item list, view-mode active state,
 *     Sandbox-locked hint pill + shake (domain-specific affordances
 *     with no canonical equivalent).
 *
 * Sandbox lock state: the navbar reads `usePilot().sandboxUnlocked`,
 * which derives directly from `currentProcessId`. No more
 * `MutationObserver` on a body class.
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {clsx} from 'clsx';
import {
  NavbarLink,
  NavbarLogo,
  NavbarWithLogoActionsAndLeftAlignedLinks,
} from '../../oatmeal/sections/navbar-with-logo-actions-and-left-aligned-links';
import {usePilot, ViewMode} from './PilotContext';
import {ThemeToggle} from './ThemeToggle';

interface NavItem {
  id: string;
  label: string;
  mode: ViewMode;
  disabled?: boolean;
  /**
   * Audience-gated visibility marker. When set, the link gets a
   * dashed ring + a tiny corner badge so the visitor can see at a
   * glance which tabs are "private" (Pitch behind the VC invite,
   * Demo / Vision behind the admin token). Doesn't gate clickability
   * — items absent from the `items` array don't render at all, so
   * if you see the tab you can use it. The marker is just a hint
   * that this surface isn't part of the default visitor experience.
   */
  access?: 'vc' | 'admin';
}

// How long the locked-Sandbox hint pill stays before fading out.
// Long enough to read; short enough to not linger.
const SANDBOX_HINT_MS = 2200;

export default function PilotNavbar() {
  const {viewMode, setViewMode, audience, isAdmin, sandboxUnlocked} = usePilot();

  const [showSandboxHint, setShowSandboxHint] = useState(false);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hintTimerRef.current !== null) {
        clearTimeout(hintTimerRef.current);
        hintTimerRef.current = null;
      }
    };
  }, []);

  // Surface the hint pill, shake the button, and scroll the process
  // picker into view as a built-in remediation path.
  const triggerSandboxHint = useCallback(() => {
    setShowSandboxHint(true);
    if (hintTimerRef.current !== null) clearTimeout(hintTimerRef.current);
    hintTimerRef.current = setTimeout(() => {
      setShowSandboxHint(false);
      hintTimerRef.current = null;
    }, SANDBOX_HINT_MS);
    setViewMode('story');
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        const picker = document.getElementById('story-cards');
        if (picker) {
          picker.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
      });
    }
  }, [setViewMode]);

  // Audience-gated item list, recomputed each render. The `visible`
  // gate is implicit — items absent from the array don't render.
  const items: NavItem[] = [
    {id: 'nav-story', label: 'Story', mode: 'story'},
    {id: 'nav-sim', label: 'Sandbox', mode: 'sandbox', disabled: !sandboxUnlocked},
    ...(audience === 'VC'
      ? [{id: 'nav-pitch', label: 'Pitch', mode: 'pitch' as ViewMode, access: 'vc' as const}]
      : []),
    ...(isAdmin
      ? [
          {id: 'nav-demo', label: 'Demo', mode: 'demo' as ViewMode, access: 'admin' as const},
          {id: 'nav-vision', label: 'Vision', mode: 'vision' as ViewMode, access: 'admin' as const},
        ]
      : []),
  ];

  // Close the canonical Oatmeal mobile dialog if it's open. The dialog
  // is owned by the vendor section; we just dismiss it after a link
  // click so the user lands on the new view rather than the dialog.
  //
  // SAFETY: the dialog id 'mobile-menu' is hard-coded by the vendor
  // section (see `oatmeal/sections/navbar-with-logo-actions-and-left-
  // aligned-links.tsx`'s `<dialog id="mobile-menu">`). This is safe
  // because only `/pilot` renders that section in the SPA today. If a
  // second Oatmeal page ever mounts the same vendor section, both
  // would collide on the global id and `getElementById` would pick
  // whichever mounted first — at that point parameterize the id via
  // a prop and pass it through to the vendor section instead of
  // editing the vendor section away from upstream.
  const closeMobileDialog = () => {
    if (typeof document === 'undefined') return;
    const dialog = document.getElementById('mobile-menu') as HTMLDialogElement | null;
    if (dialog && typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    }
  };

  const handleNavClick = (item: NavItem) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (item.disabled) {
      if (item.mode === 'sandbox') triggerSandboxHint();
      closeMobileDialog();
      return;
    }
    // Clicking Story while already in Story or Sandbox should land the
    // visitor at the top of Story (not preserve their current scroll —
    // a no-op click reads as broken). Per the May 2026 functional/
    // content review: "Story navigation — clicking on Story at the top
    // does nothing".
    if (item.mode === 'story' && typeof window !== 'undefined') {
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
    setViewMode(item.mode);
    closeMobileDialog();
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setViewMode('story');
    if (typeof window !== 'undefined') {
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
    closeMobileDialog();
  };

  // Demo + Vision take over the full viewport with a dark canvas; the
  // navbar's default cream `bg-taupe-100/85` would strip across that
  // canvas and read as wrong-mode. F17 Slice C: switch the navbar's
  // bg / link colors / active state from React state instead of the
  // prior `body.demo-mode .mp-topbar` / `body.vis-mode .mp-topbar`
  // CSS rules in pilot-engine.css.
  const isDarkCanvas = viewMode === 'demo' || viewMode === 'vision';

  // Active state styling: canonical NavbarLink renders text-taupe-950
  // (highest contrast) by default and the kit assumes traditional
  // anchor navigation where active = current page. For SPA mode-
  // switching we visualize "active" as the high-contrast variant and
  // "inactive" as taupe-500 dimmed. Mobile dialog (≥lg breakpoint
  // hidden) keeps the bold variant for both since size carries the
  // affordance.
  // On dark canvas (demo/vision) the active variant uses a translucent
  // white pill rather than a solid white bg so it reads on the dark
  // ground; the inactive variant goes taupe-300.
  const linkClassFor = (item: NavItem, isActive: boolean) =>
    clsx(
      'topnav-btn relative transition-colors rounded-full px-3 py-1',
      // `!text-...` (Tailwind v4 important suffix) is required on
      // the dark-canvas branches because the vendor NavbarLink
      // emits `text-taupe-950 dark:text-white` ahead of our class
      // string, and Tailwind v4's category-based sort puts
      // `text-taupe-950` AFTER `text-taupe-300` in the generated
      // CSS so the vendor color wins. The `!` flips specificity so
      // our explicit "this nav is on a dark background" color
      // overrides the vendor default.
      isDarkCanvas
        ? isActive
          ? '[background:oklch(98.6%_0.002_67.8/0.12)] !text-taupe-50'
          : '!text-taupe-300 hover:!text-taupe-50'
        : isActive
          ? 'bg-white text-taupe-950 shadow-[0_1px_2px_rgba(0,0,0,0.05)] dark:bg-taupe-800 dark:text-white'
          : 'text-taupe-600 hover:text-taupe-950 dark:text-taupe-400 dark:hover:text-white',
      item.disabled && 'opacity-50 cursor-not-allowed',
      // Audience-gated tabs get a dashed accent ring so the visitor
      // can see at a glance which tabs are private. Colour-coded:
      // brand-2 (orange-ish) for VC, brand (pink-ish) for admin —
      // matches the chip palette demo.js uses for the same two
      // primitives. Active state suppresses the dashed ring because
      // the solid bg already separates it from the rest.
      !isActive && item.access === 'vc' &&
        '[outline:1px_dashed_oklch(82%_0.15_35/0.7)] [outline-offset:2px]',
      !isActive && item.access === 'admin' &&
        '[outline:1px_dashed_oklch(70%_0.18_15/0.7)] [outline-offset:2px]',
    );

  // Tiny corner badge for audience-gated tabs. Sits above the pill,
  // matches the ring colour. Renders only when access is set AND the
  // tab isn't currently active (no need for the badge when the bg
  // already screams "I'm here").
  const accessBadge = (item: NavItem, isActive: boolean) => {
    if (!item.access || isActive) return null;
    const label = item.access === 'vc' ? 'VC' : 'ADMIN';
    const colorClass =
      item.access === 'vc'
        ? '[background:oklch(82%_0.15_35)] text-white'
        : '[background:oklch(70%_0.18_15)] text-white';
    return (
      <span
        aria-hidden
        className={clsx(
          'absolute -top-2 -right-1 inline-flex items-center rounded-full px-1.5 py-px text-[8px] font-semibold uppercase tracking-wider leading-none',
          colorClass,
        )}
      >
        {label}
      </span>
    );
  };

  const links = (
    <>
      {items.map((item) => {
        const isActive = item.mode === viewMode;
        const accessHint =
          item.access === 'vc'
            ? ' (VC-only)'
            : item.access === 'admin'
              ? ' (admin-only)'
              : '';
        return (
          <NavbarLink
            key={item.id}
            id={item.id}
            href="#"
            onClick={handleNavClick(item)}
            aria-current={isActive ? 'page' : undefined}
            aria-disabled={item.disabled ? 'true' : undefined}
            aria-label={`${item.label}${accessHint}`}
            className={linkClassFor(item, isActive)}
          >
            {item.label}
            {accessBadge(item, isActive)}
          </NavbarLink>
        );
      })}
    </>
  );

  const logo = (
    <NavbarLogo
      href="#/pilot"
      onClick={handleLogoClick}
      aria-label="Planapus, back to start"
      className={clsx(
        'font-display text-2xl/8 font-normal tracking-tight',
        isDarkCanvas ? 'text-taupe-50' : 'text-taupe-950 dark:text-white',
      )}
    >
      Planapus
    </NavbarLogo>
  );

  const actions = (
    <div className="flex items-center gap-3">
      {/* ThemeToggle gated behind admin access. Default visitor
          experience is light theme (see useThemePreference's
          `readStored` default); admins keep the system/light/dark
          control for previewing styling work. */}
      {isAdmin && <ThemeToggle />}
      <button
        id="topbar-talk"
        type="button"
        className={clsx(
          'topbar-cta inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-4 py-2 text-sm/6 font-medium',
          isDarkCanvas
            ? 'bg-taupe-100 text-taupe-950 hover:bg-taupe-200'
            : 'bg-taupe-950 text-white hover:bg-taupe-800 dark:bg-taupe-300 dark:text-taupe-950 dark:hover:bg-taupe-200',
        )}
      >
        Talk to us
        <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
          <polygon points="6,3 11,8 6,13" />
        </svg>
      </button>
    </div>
  );

  return (
    <>
      <NavbarWithLogoActionsAndLeftAlignedLinks
        id="navbar"
        // Dark-canvas overrides live here as React-driven utilities
        // instead of `body.demo-mode .mp-topbar` CSS gates. The bg
        // is SOLID (not `/0.85` translucent) — the Oatmeal default
        // pairs `bg-taupe-100/85` with backdrop-blur, which against
        // the dark demo/vision scroller behind it can render the
        // navbar as nearly transparent / "disappeared" depending on
        // what's currently scrolled under it. Solid bg + a slightly
        // brighter bottom border guarantee a clear visual divider in
        // every scroll position.
        //
        // `!z-50 isolate`: the Oatmeal vendor sets `z-10` on the
        // <header> and demo.js paints a `.demo-dots` strip with
        // `z-50` in the ROOT stacking context (its containing block
        // — the demo shell — has no z-index so doesn't form its own
        // context). On dark-canvas modes that z-50 strip could win
        // over the navbar's z-10 in the same root context, sometimes
        // intercepting clicks on the navbar links even though the
        // strip itself sits mid-viewport. Bumping the navbar to z-50
        // and giving it its own stacking context via `isolate`
        // guarantees the header sits above any same-level descendant
        // of <main> regardless of what z-index they paint themselves
        // with.
        className={clsx(
          'mp-topbar !z-50 isolate',
          isDarkCanvas &&
            '![background:oklch(14.7%_0.004_49.3)] ![backdrop-filter:none] ![border-bottom-color:oklch(98.6%_0.002_67.8/0.18)]',
        )}
        logo={logo}
        links={links}
        actions={actions}
      />
      {showSandboxHint && (
        <div
          className="pp-nav-sim-hint fixed left-1/2 top-20 z-40 -translate-x-1/2 rounded-full bg-taupe-950 px-4 py-2 text-sm/6 text-white shadow-lg dark:bg-taupe-200 dark:text-taupe-950"
          role="status"
          aria-live="polite"
        >
          Pick a use case in Story to unlock Sandbox
        </div>
      )}
    </>
  );
}
